import store from '@store';
import i18n from '@assets/i18n/index';
import type { NavigationHookAfter, NavigationGuardWithThis } from 'vue-router';
import { useProfileList } from '@models/profile/profileList';
import { useAccountInfo } from '@models/account/useAccountInfo';
import { useAllOnlinePackage } from '@models/package/useAllOnlinePackage';
import { logout } from '@master/hooks/service/useLogin';
import analyticsService from '@assets/services/analytics.service';
import {
    hasLoggedIn,
    clearLocalUserInfo,
    updateLocalToken,
    clearPtiTimeInfo
} from '@master/utils/account.util';
import { changeProfile } from '@models/profile/profile';
import { Product, getProductList } from '@models/profile/useCurrentProfile';
import useShowAuthDialog from '@master/hooks/service/useShowAuthDialog';
import { usePermission } from '@models/profile/useProfilePermission';
import { isLoginFromWidget, isLoginFromHeatmap } from '@master/hooks/service/useLogin';

interface RouterService {
    afterChange: NavigationHookAfter;
    beforeChange: NavigationGuardWithThis<undefined>;
    beforeEnterMain: NavigationGuardWithThis<undefined>;
    beforeEnterCheckLoggedIn: NavigationGuardWithThis<undefined>;
    beforeEnterLogout: NavigationGuardWithThis<undefined>;
    beforeEnterProfileList: NavigationGuardWithThis<undefined>;
    beforeEnterSignup: NavigationGuardWithThis<undefined>;
    beforeEnterInsight: NavigationGuardWithThis<undefined>;
    beforeEnterExperience: NavigationGuardWithThis<undefined>;
    beforeEnterInsightPlan: NavigationGuardWithThis<undefined>;
    beforeEnterHome: NavigationGuardWithThis<undefined>;
    beforeEnterProduct: NavigationGuardWithThis<undefined>;
}

const { account, getAccountInfo } = useAccountInfo();
const { getAllOnlinePackage } = useAllOnlinePackage();
const {
    profileList,
    profileListPackageInfo,
    profileListWithoutDemo,
    getProfileList,
    getProfileListPackageInfo
} = useProfileList();
const { checkAuth } = useShowAuthDialog();
const { hasHomePermission } = usePermission();

const routerService: RouterService = {
    beforeChange: (to, from, next) => {
        if (to.meta.isWhiteList) {
            next();
            return;
        }

        if (!hasLoggedIn()) {
            logout('no_token', 'router_before_change');
            return;
        }

        store.commit('master/main/SET_PAGE_LOADING_STATUS', true);

        if (account.value) {
            next();
        } else {
            getAccountInfo(
                () => {
                    getAllOnlinePackage();
                    next();
                },
                () => {
                    logout('accountInfo_failed', 'get_account_failed');
                    store.commit('master/main/SET_PAGE_LOADING_STATUS', false);
                }
            );
        }
    },

    afterChange: to => {
        let path = to.fullPath;
        to?.params?.sid && (path = path.replace(to?.params?.sid as string, 'sid'));
        to?.params?.experienceId &&
            (path = path.replace(to?.params?.experienceId as string, 'experienceId'));
        const { title, titleI18n, hideMainMenu } = to.meta;
        const pageTitle = title || (titleI18n && i18n?.global?.t?.(titleI18n as string));

        pageTitle &&
            (document.title = pageTitle + (to.path.includes('landing_page') ? '' : ' | Ptengine'));

        setTimeout(() =>
            analyticsService
                .useMixpanel('page_view', {
                    home_version: hasHomePermission.value ? '02' : '01',
                    path: path // 加path参数
                })
                .track()
        );
        store.commit('master/main/SET_PAGE_LOADING_STATUS', false);
    },

    beforeEnterMain: (to, from, next) => {
        const gotoMain = () => {
            changeProfile(String(to.params.sid), () => {
                const galleryTemplate = localStorage.getItem('gallery_template');
                if (galleryTemplate) {
                    localStorage.removeItem('gallery_template');
                    // 直接进入Ptx
                    const experienceProduct = getProductList().find((product: Product) => {
                        return product.key === 'experience';
                    });
                    const profileInfo = profileList.value?.find(
                        profile => profile.sid === to.params.sid
                    );
                    if (profileInfo) {
                        experienceProduct &&
                            checkAuth(
                                experienceProduct,
                                profileInfo,
                                function () {
                                    next({
                                        name: experienceProduct.routerName,
                                        params: {
                                            sid: to.params.sid,
                                            template: galleryTemplate,
                                            needQuery: 'true'
                                        }
                                    });
                                },
                                function () {
                                    next();
                                }
                            );
                    }
                } else {
                    next();
                }
            });
        };
        if (to.params.sid) {
            if (profileList.value && profileListPackageInfo.value) {
                gotoMain();
            } else {
                getProfileList(
                    () => {
                        getProfileListPackageInfo(
                            () => {
                                gotoMain();
                            },
                            () => {
                                next({ name: 'ProfileList' });
                            }
                        );
                    },
                    () => next({ name: 'ProfileList' })
                );
            }
            return;
        }
        next({ name: 'ProfileList' });
    },

    beforeEnterCheckLoggedIn: (to, from, next) => {
        const _shopify = to.query.shopifyid;
        const { shopifydomain } = to.query;

        // 判断登陆来源，如果是shopify，存储url中的携带的参数
        if (_shopify && typeof _shopify === 'string') {
            localStorage.setItem('shopifyid', _shopify);
        }
        if (shopifydomain && typeof shopifydomain === 'string') {
            localStorage.setItem('shopifydomain', shopifydomain);
        }

        if (to.name === 'Login' && to.query?.token) {
            updateLocalToken(to.query.token as string, to.query.uid as string);
        }

        if (hasLoggedIn()) {
            if (isLoginFromWidget() || isLoginFromHeatmap()) {
                // widget & 弹出热图 直接进入登录页
                clearLocalUserInfo('widgetLogin');
            } else {
                next({
                    name: 'ProfileList',
                    query: { from: 'login' }
                });
                clearPtiTimeInfo();
                return;
            }
        }

        next();
    },

    beforeEnterLogout: (to, from, next) => {
        clearLocalUserInfo('beforeEnterLogout');
        setTimeout(() => {
            next();
        }, 100);
    },

    beforeEnterProfileList: (to, from, next) => {
        const getProfilesAfter = () => {
            const galleryTemplate = localStorage.getItem('gallery_template');
            const shopify = localStorage.getItem('shopifyid');
            if (shopify) {
                // 走shopify创建流程，如果存在shopify档案，直接进入档案
                const profileInfo = profileList.value?.find(profile => profile.additionalId);
                if (profileInfo) {
                    // 直接进shopify档案
                    localStorage.removeItem('shopifyid');
                    localStorage.removeItem('shopifydomain');
                    next({
                        name: 'ProductList',
                        params: {
                            sid: profileInfo.sid
                        }
                    });
                } else {
                    // 开始创建shopify档案
                    next({
                        name: 'CreateProject'
                    });
                }
                return;
            }
            if (profileListWithoutDemo.value?.length === 0) {
                next({
                    name: 'CreateProject'
                });
            } else if (profileListWithoutDemo.value?.length && galleryTemplate) {
                localStorage.removeItem('gallery_template');
                // 默认sid
                const sid = profileListWithoutDemo.value?.[0].sid;
                const profileInfo = profileListWithoutDemo.value.find(
                    profile => profile.sid === sid
                );
                if (!profileInfo) {
                    next();
                    return;
                }
                if (profileListWithoutDemo.value?.length === 1) {
                    // 直接进入Ptx
                    const experienceProduct = getProductList().find((product: Product) => {
                        return product.key === 'experience';
                    });

                    experienceProduct &&
                        checkAuth(
                            experienceProduct,
                            profileInfo,
                            function () {
                                next({
                                    name: experienceProduct.routerName,
                                    params: {
                                        sid: sid,
                                        template: galleryTemplate
                                    }
                                });
                            },
                            function () {
                                next();
                            }
                        );
                } else {
                    // 进入档案选择页面，档案选择后，继续走模版创建流程
                    localStorage.setItem('gallery_template', galleryTemplate);
                    next();
                }
            } else if (
                (from.name === 'Login' ||
                    to.query.from === 'login' ||
                    to.query.from === 'singup') &&
                profileListWithoutDemo.value?.length === 1
            ) {
                next({
                    name: 'ProductList',
                    params: {
                        sid: profileListWithoutDemo.value?.[0].sid
                    }
                });
            } else {
                next();
            }
        };

        if (profileList.value && profileListPackageInfo.value) {
            getProfilesAfter();
        } else {
            getProfileList(
                () => {
                    getProfileListPackageInfo(
                        () => getProfilesAfter(),
                        () => {
                            logout('accountInfo_failed', 'before_enter_profile_list');
                        }
                    );
                },
                () => logout('accountInfo_failed', 'before_enter_profile_list')
            );
        }
    },

    /**
     * 进入注册页逻辑
     * 欧美注册第三步，刷新时。判断登录信息，如果未登录，则去登录页，否则进入产品。
     * 其他步骤，去除query中的step字段
     */
    beforeEnterSignup: (to, from, next) => {
        const queryStepString = to.query.step;
        if (queryStepString === 'installTrackCode') {
            next({
                name: hasLoggedIn() ? 'ProfileList' : 'Login',
                query: {
                    from: hasLoggedIn() ? 'singup' : ''
                }
            });
            return;
        }
        clearLocalUserInfo('beforeEnterSignup');
        setTimeout(() => {
            if (queryStepString) {
                const query = Object.assign({}, to.query);
                delete query.step;
                next({
                    name: to?.name || 'Signup',
                    query
                });
                return;
            }
            next();
        }, 50);
    },

    beforeEnterInsight: (to, from, next) => {
        analyticsService.sendProductActiveTime('pti');
        next();
    },

    beforeEnterExperience: (to, from, next) => {
        analyticsService.sendProductActiveTime('ptx');
        next();
    },

    beforeEnterInsightPlan: (to, from, next) => {
        if (profileList.value) {
            next();
        } else {
            getProfileList(
                () => {
                    getProfileListPackageInfo(
                        () => next(),
                        () => {
                            logout('accountInfo_failed', 'before_enter_insight_plan');
                        }
                    );
                },
                () => logout('accountInfo_failed', 'before_enter_insight_plan')
            );
        }
    },

    beforeEnterHome: (to, from, next) => {
        if (hasHomePermission.value) {
            next();
        } else {
            next({
                name: 'ProductList',
                params: {
                    sid: to.params.sid
                }
            });
        }
    },

    beforeEnterProduct: (to, from, next) => {
        if (hasHomePermission.value) {
            next({
                name: 'Home',
                params: {
                    sid: to.params.sid
                }
            });
        } else {
            next();
        }
    }
};
export default routerService;
